import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { object, string } from 'yup';
import {
    Button,
    ErrorMessage,
    Label,
    TextField,
    locationData,
    validationRules,
    clean,
    extractErrors,
} from '../../../Common/Form';
import Select from 'react-select';

const { password, confirmPassword, memberCompany, memberTitle, country, timezone, phone } = validationRules;
const schema = object({
    password,
    confirmPassword,
    memberCompany,
    memberTitle,
    country,
    timezone,
    phone,
    memberAgreedToTerms: string().required('The Agree To Terms field is required'),
});

const StepTwo = ({ collectData }) => {
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const onSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setIsSubmitting(true);

            const data = Object.fromEntries(new FormData(e.currentTarget).entries());

            await schema
                .validate(data, { strict: true, abortEarly: false })
                .then(() => {
                    setErrors({});
                    collectData({
                        ...data,
                        memberCompany: clean(data.memberCompany),
                        memberTitle: clean(data.memberTitle),
                    });
                })
                .catch((errs) => {
                    setErrors(extractErrors(errs));
                    setIsSubmitting(false);
                });
        },
        [collectData]
    );

    return (
        <form onSubmit={onSubmit}>
            <div className="grid -col2 -form">
                <div className="grid__cell">
                    <TextField id="password" label="Password" required type="password" />
                    <ErrorMessage message={errors.password ?? ''} />
                </div>
                <div className="grid__cell">
                    <TextField id="confirmPassword" label="Confirm Password" required type="password" />
                    <ErrorMessage message={errors.confirmPassword ?? ''} />
                </div>
            </div>
            <div className="grid -col2 -form">
                <div className="grid__cell">
                    <TextField id="memberCompany" label="Company" required />
                    <ErrorMessage message={errors.memberCompany ?? ''} />
                </div>
                <div className="grid__cell">
                    <TextField id="memberTitle" label="Job Title" required />
                    <ErrorMessage message={errors.memberTitle ?? ''} />
                </div>
            </div>
            <div>
                <Label id="country" text="Country" required />
                <Select
                    className="v-select select__options vs--single vs--searchable"
                    classNamePrefix="vs"
                    isClearable
                    name="country"
                    placeholder=""
                    options={locationData.countries}
                />
                <ErrorMessage message={errors.country ?? ''} />
            </div>
            <div>
                <Label id="timezone" text="Time Zone" required />
                <Select
                    className="v-select select__options vs--single vs--searchable"
                    classNamePrefix="vs"
                    isClearable
                    name="timezone"
                    placeholder=""
                    options={locationData.timezones}
                />
                <ErrorMessage message={errors.timezone ?? ''} />
            </div>
            <div>
                <div className="text">
                    <TextField id="phone" label="Phone" required />
                    <ErrorMessage message={errors.phone ?? ''} />
                </div>
            </div>
            <div className="checkboxes">
                <div className="checkbox">
                    <input
                        title="Agree To Terms"
                        id="agreeToTerms"
                        name="memberAgreedToTerms"
                        type="checkbox"
                        className="checkbox__input"
                    />
                    <label htmlFor="agreeToTerms" className="checkbox__label">
                        <span className="required">* You must check this box to continue.</span>
                        <br />
                        By checking this box, you consent to receive emails from Cutter Associates. You may opt out of
                        these emails at any time. By checking this box, you also certify that you are an employee or
                        staff contractor in a staff augmentation role of the above-named firm, and that you agree to
                        abide by the terms of the firm's Cutter Membership Agreement, which prohibits you from sharing
                        access to any Cutter research and virtual or in-person events with any vendor, contractor,
                        consultant or third party provider of or to your firm. For further information, please consult
                        the{' '}
                        <a href="/privacy" target="_blank">
                            Privacy Policy
                        </a>{' '}
                        and
                        <a href="/terms-of-use" target="_blank">
                            Terms of Use Policy
                        </a>
                    </label>
                </div>
            </div>
            <ErrorMessage message={errors.memberAgreedToTerms ?? ''} />

            <ErrorMessage message={Object.keys(errors).length > 0 ? 'Please correct the invalid fields above.' : ''} />

            <div className="grid -col2 -action -vAlignCenter">
                <div className="grid__cell">
                    <a className="link -underline" href="/register">
                        Back
                    </a>
                </div>
                <div className="grid__cell -button">
                    <Button disabled={isSubmitting} text="Complete" />
                </div>
            </div>
        </form>
    );
};

StepTwo.propTypes = {
    collectData: PropTypes.func,
};

export default StepTwo;
